import React, { useState, useEffect } from 'react';
import { Wallet, Plus, Trash2, Edit2, Copy, CheckCircle } from 'lucide-react';
import { systemWalletAPI } from '../services/api';

const SystemWallets = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ label: '', currency: 'BTC', address: '', network: '' });
  const [editingId, setEditingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const fetchWallets = async () => {
    try {
      const res = await systemWalletAPI.getAllSystemWallets();
      setWallets(res.data.message || res.data.wallets || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallets();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await systemWalletAPI.editSystemWallet(editingId, formData);
      } else {
        await systemWalletAPI.createSystemWallet(formData);
      }
      setShowModal(false);
      setFormData({ label: '', currency: 'BTC', address: '', network: '' });
      setEditingId(null);
      fetchWallets();
    } catch (err) {
      alert(err.response?.data?.message || 'Error saving wallet');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this wallet?")) {
      try {
        await systemWalletAPI.deleteSystemWallet(id);
        fetchWallets();
      } catch (err) {
        alert("Error deleting wallet");
      }
    }
  };

  const handleCopy = (address, id) => {
    navigator.clipboard.writeText(address);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header" style={{ marginBottom: '2rem' }}>
        <div>
          <h1>Global System Wallets</h1>
          <p>Manage the deposit addresses provided to users.</p>
        </div>
        <button 
          className="btn btn-primary glass" 
          onClick={() => { setEditingId(null); setFormData({ label: '', currency: 'BTC', address: '', network: '' }); setShowModal(true); }}
        >
          <Plus size={18} style={{ marginRight: '0.5rem' }}/> Add Wallet
        </button>
      </div>

      <div className="dashboard-panel">
        <h2 className="panel-title mb-3">Active Deposit Addresses</h2>
        {loading ? (
          <div className="spinner" style={{ margin: '2rem auto' }}></div>
        ) : wallets.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No system wallets found. Add one to start receiving deposits.</p>
        ) : (
          <div className="premium-table-container">
            <table className="premium-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Currency</th>
                  <th>Network</th>
                  <th>Address</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {wallets.map(w => (
                  <tr key={w._id}>
                    <td><strong>{w.label}</strong></td>
                    <td><span className="status-badge" style={{ background: 'rgba(252, 213, 53, 0.1)', color: '#FCD535', border: '1px solid rgba(252, 213, 53, 0.2)' }}>{w.currency}</span></td>
                    <td style={{ color: 'var(--text-secondary)' }}>{w.network || 'Native'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <code style={{ background: 'var(--bg-primary)', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.8rem' }}>
                          {w.address.substring(0, 8)}...{w.address.substring(w.address.length - 8)}
                        </code>
                        <button className="icon-btn" style={{ width: '24px', height: '24px' }} onClick={() => handleCopy(w.address, w._id)}>
                          {copiedId === w._id ? <CheckCircle size={14} className="text-success" /> : <Copy size={14} />}
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="icon-btn-circle" onClick={() => { setEditingId(w._id); setFormData(w); setShowModal(true); }}>
                          <Edit2 size={16} />
                        </button>
                        <button className="icon-btn-circle" style={{ color: '#ef4444' }} onClick={() => handleDelete(w._id)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="sidebar-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowModal(false)}>
          <div className="dashboard-panel" style={{ width: '100%', maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <div className="panel-header">
              <h2 className="panel-title">{editingId ? 'Edit Wallet' : 'Add New Wallet'}</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Label (e.g., Main BTC Wallet)</label>
                <input type="text" value={formData.label} onChange={e => setFormData({...formData, label: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Currency</label>
                <select value={formData.currency} onChange={e => setFormData({...formData, currency: e.target.value})} required>
                  <option value="BTC">BTC (Bitcoin)</option>
                  <option value="ETH">ETH (Ethereum)</option>
                  <option value="USDT">USDT (Tether)</option>
                  <option value="USDC">USDC (USD Coin)</option>
                  <option value="BNB">BNB (Binance Coin)</option>
                  <option value="XRP">XRP (Ripple)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Network (e.g., ERC-20, TRC-20)</label>
                <input type="text" value={formData.network} onChange={e => setFormData({...formData, network: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Wallet Address</label>
                <input type="text" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} required />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button type="button" className="btn" style={{ flex: 1, background: 'var(--bg-primary)', color: 'var(--text-primary)' }} onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Save Wallet</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemWallets;
